import Autocomplete from '@mui/material/Autocomplete';
import { useField } from 'formik';
import { FormControl, TextField } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useCallback, useMemo } from 'react';
import useGetCombinedEvents from './hooks/useGetCombinedEvents';
import { FORM_MODES } from '../../utils';
import { ItemWithLabel } from '../../components';
import useGetCombinedAttributes from './hooks/useGetCombinedAttributes';
import { useGlobalization } from '@innovyze/stylovyze/lib/contexts/GlobalizationProvider';
import { useMode } from './contexts/StylovyzeFormContext';
import ListBox from './ListBox';
export default function InputAutocomplete({ options, hiddenOptions = [], autocompleteProps, renderInput, value: key, fieldAttrs, onChange, onClear, onDebounceChange, networkError, label, dataCy, textFieldProps, helperText, error, ...props }) {
    const { t } = useGlobalization();
    const mode = useMode();
    const [field, , helpers] = useField(fieldAttrs.name);
    const combinedEvents = useGetCombinedEvents({
        fieldAttrs,
        onChange,
        onDebounceChange,
        networkError,
        onBlur: e => {
            helpers.setTouched(true);
            autocompleteProps?.onBlur && autocompleteProps?.onBlur(e);
            field.onBlur && field.onBlur(e);
        },
    });
    const combinedAttributes = useGetCombinedAttributes({
        networkError,
        helperText,
        error: error,
    });
    const getOptionLabel = useCallback((option) => {
        if (option) {
            if (option.label)
                return option.label;
            if (option.value)
                return option.value;
        }
        return '';
    }, []);
    const getOptionSelected = useCallback((opt, val) => {
        if (!val.key)
            return false;
        const o = typeof opt === 'string' ? opt : opt.key;
        const v = typeof val === 'string' ? val : val.key;
        return o === v;
    }, []);
    const hashedOptions = useMemo(() => {
        const optionsHash = {};
        for (let i = 0; i < options.length; i++) {
            const opt = options[i];
            optionsHash[typeof opt === 'string' ? opt : opt.key] =
                typeof opt === 'string' ? opt : opt.value;
        }
        for (let i = 0; i < (hiddenOptions ?? []).length; i++) {
            const opt = hiddenOptions[i];
            optionsHash[opt.key] = opt.value;
        }
        return optionsHash;
    }, [options, hiddenOptions]);
    const currentValue = useMemo(() => {
        if (key) {
            const value = hashedOptions[key];
            return {
                key: key,
                value: value ?? key,
            };
        }
        if (field.value) {
            if (typeof field.value === 'string') {
                const value = hashedOptions[field.value];
                return {
                    key: field.value,
                    value: value ?? field.value,
                };
            }
            return field.value;
        }
        return null;
    }, [key, field.value, hashedOptions]);
    const useStyles = makeStyles({
        listbox: {
            boxSizing: 'border-box',
            '& ul': {
                padding: 0,
                margin: 0,
            },
        },
    });
    const classes = useStyles();
    return (React.createElement(FormControl, { variant: "outlined", margin: mode === FORM_MODES.VIEW ? 'none' : 'normal', fullWidth: true }, mode === FORM_MODES.VIEW ? (React.createElement(ItemWithLabel, { label: label, value: getOptionLabel(currentValue) })) : (React.createElement(Autocomplete, { classes: classes, getOptionLabel: getOptionLabel, isOptionEqualToValue: props.getOptionSelected ?? getOptionSelected, options: options, ListboxComponent: ListBox, autoHighlight: true, renderOption: (props, option) => {
            return React.createElement("li", { ...props }, getOptionLabel(option));
        }, autoSelect: true, value: currentValue, "data-cy": dataCy, renderInput: renderInput
            ? renderInput
            : params => (React.createElement(TextField, { ...textFieldProps, ...combinedAttributes, ...params, label: label, variant: "outlined" })), onInputChange: (e, newInputValue, reason) => {
            if (reason === 'clear') {
                if (onClear)
                    onClear(e, newInputValue, reason);
            }
        }, loadingText: t('Loading...', {
            context: 'Input autocomplete loading text',
        }), noOptionsText: t('No options', {
            context: 'Input autocomplete no options',
        }), openText: t('Open', {
            context: 'Input autocomplete open popup icon button',
        }), closeText: t('Close', {
            context: 'Input autocomplete close popup icon button',
        }), clearText: t('Clear', {
            context: 'Input autocomplete clear icon button',
        }), ...autocompleteProps, 
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onChange: (e, option) => {
            if (option && option.key) {
                helpers.setValue(option.key);
            }
            else {
                helpers.setValue('');
            }
            if (combinedEvents.onChange) {
                combinedEvents.onChange(e, option);
            }
        }, onBlur: combinedEvents.onBlur, onFocus: combinedEvents.onFocus }))));
}
